/* eslint-disable no-shadow */
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { extractTypeFor, getEventFieldPath } from '../utils/common';
import login, * as fromLogin from './login';
import config, * as fromConfig from './config';
import funnel, * as fromFunnel from './funnel/funnel';
import globalFilter, * as fromGlobalFilter from './globalFilter';
import segment, * as fromSegment from './segment/segment';
import event, * as fromEvent from './event/event';
import location, * as fromLocation from './location';
import previousLocation, * as fromPreviousLocation from './previousLocation';
import project, * as fromProject from './project';
import retention, * as fromRetention from './retention/retention';
import retentionComplex, * as fromRetentionComplex from './retentionComplex/retentionComplex';
import fieldPaths, * as fromFieldPaths from './fieldPaths';
import mapping, * as fromMapping from './mapping/mapping';
import authorization, * as fromAuthorization from './authorization/authorization';
import application, * as fromApplication from './application';
import usage, * as fromUsage from './usage/usage';
import * as fromFilter from './filter';
import revenue, * as fromRevenue from './revenue/revenue';
import users, * as fromUsers from './users/users';
import labels, * as fromLabels from './labels';
import cache, * as fromCache from './cache';
import meta, * as fromMeta from './meta';
import dashboards, * as fromDashboards from './dashboard/dashboards';
import reports, * as fromReports from './reports';
import jobIds, * as fromJobIds from './jobIds';

const analyticsApp = combineReducers({
  config,
  login,
  globalFilter,
  funnel,
  dashboards,
  segment,
  event,
  location,
  previousLocation,
  project,
  retentionComplex,
  retention,
  fieldPaths,
  mapping,
  authorization,
  application,
  usage,
  revenue,
  users,
  labels,
  cache,
  meta,
  reports,
  jobIds,
});

const rootReducer = (state, action) => {
  let stateVar = state;

  if (action.type === 'RESET_APP') {
    // authorization хранит permissionsByTenant из /me — глобальные данные пользователя, а не
    // per-tenant кэш; сохраняем при сбросе, иначе после смены tenant'а меню остаётся без прав.
    const {
      login,
      config,
      location,
      authorization,
      dashboards: { execute },
    } = stateVar;
    stateVar = { config, login, location, authorization, dashboards: { execute } };
  }

  return analyticsApp(stateVar, action);
};

export default rootReducer;

export const getApplicationBackendUrl = (state) =>
  fromConfig.getApplicationBackendUrl(state.config);

export const getApplicationDocsUrl = (state) => fromConfig.getApplicationDocsUrl(state.config);

export const getTenantId = (state) => fromLogin.getTenantId(state.login);

export const getTenants = (state) => fromLogin.getTenants(state.login);

export const isAuthenticated = (state) => fromLogin.isAuthenticated(state.login);

export const isAuthChecked = (state) => fromLogin.isAuthChecked(state.login);

export const getSessionExpiresAt = (state) => fromLogin.getSessionExpiresAt(state.login);

export const getMetainf = (state, event) => fromEvent.getMetainf(state.event, event);

export const extractType = (state, event, field) => extractTypeFor(field, getMetainf(state, event));

export const getEventNames = (state) => fromEvent.getEventNames(state.event);

export const getFieldPathsFor = (state, entry) => fromEvent.getFieldPathsFor(state.event, entry);

export const getFieldPathKeys = (state) => fromEvent.getFieldPathKeys(state.event);

export const isMetainfFetching = (state) => fromEvent.isMetainfFetching(state.event);

export const getGlobalFilter = (state) => state.globalFilter;

export const areFunnelsFetching = (state) => fromFunnel.areFetching(state.funnel);

export const getFunnels = (state) => fromFunnel.getFunnels(state.funnel);

export const getSelectedFunnel = (state) => fromFunnel.getSelected(state.funnel);

export const getFunnelSQL = (state) => fromFunnel.getFunnelSQL(state.funnel);

export const getFunnelExposureError = (state) => fromFunnel.getError(state.funnel);

/*
 * Clarification: this method checks whether we wait a response from a server. It doesn't
 * check whether we modify/add a funnel
 */
export const isFunnelUpdating = (state) => fromFunnel.isUpdating(state.funnel);

export const getUpdatingFunnel = (state) => fromFunnel.getUpdating(state.funnel);

export const getUpdatingFunnelId = (state) => fromFunnel.getUpdatingId(state.funnel);

export const isFunnelDeleting = (state) => fromFunnel.isDeleting(state.funnel);

export const getFunnelExecutionResult = (state) => fromFunnel.getExecutionResult(state.funnel);

export const getFunnelExecutionTime = (state) => fromFunnel.getExecutionTime(state.funnel);

export const getFunnelConversionInterval = (state) =>
  fromFunnel.getConversionInterval(state.funnel);

export const getFunnelView = (state) => fromFunnel.getView(state.funnel);

export const isFunnelManualEnabled = (state) => fromFunnel.isManualEnabled(state.funnel);

export const getFunnelFilterValue = (state) => fromFunnel.getFilterValue(state.funnel);

export const getFunnelSideListContainerWidth = (state) =>
  fromFunnel.getSideListContainerWidth(state.funnel);

export const isFunnelExists = (state, funnel) => fromFunnel.isFunnelExists(state.funnel, funnel);

export const isFunnelExecuting = (state) => fromFunnel.isExecuting(state.funnel);

export const getFunnelExecuteId = (state) => fromFunnel.getExecuteId(state.funnel);

const filterCompoundItemsByName = (items, searchText) => {
  if (searchText) {
    return items.filter((item) => {
      return item?.name.toLowerCase().includes(searchText?.toLowerCase());
    });
  }

  return items;
};

export const getFilteredFunnels = createSelector(
  [getFunnels, getFunnelFilterValue],
  filterCompoundItemsByName
);

export const areSegmentsFetching = (state) => fromSegment.areFetching(state.segment);

export const getSegments = (state) => fromSegment.getSegments(state.segment);

export const isSegmentUpdating = (state) => fromSegment.isUpdating(state.segment);

export const getUpdatingSegment = (state) => fromSegment.getUpdating(state.segment);

export const getUpdatingSegmentId = (state) => fromSegment.getUpdatingId(state.segment);

export const isSegmentDeleting = (state) => fromSegment.isDeleting(state.segment);

export const getSegmentFilterValue = (state) => fromSegment.getFilterValue(state.segment);

export const getFilteredSegments = createSelector(
  [getSegments, getSegmentFilterValue],
  filterCompoundItemsByName
);

export const getSelectedSegment = (state) => fromSegment.getSelected(state.segment);

export const getSegmentSideListContainerWidth = (state) =>
  fromSegment.getSideListContainerWidth(state.segment);

export const isSegmentExists = (state, segment) =>
  fromSegment.isSegmentExists(state.segment, segment);

export const getGlobalFilterEvent = (state) => fromGlobalFilter.getEvent(state.globalFilter);

export const getGlobalFilterClauses = (state) => fromFilter.getClauses(state.globalFilter);

export const getGlobalFilterConcatOperator = (state) =>
  fromFilter.getConcatOperator(state.globalFilter);

export const getUsersEvent = (state) => fromUsers.getEvent(state.users);

export const getUsersColumns = (state) => fromUsers.getColumns(state.users);

export const getEventsExecuteTime = (state) => fromEvent.getEventsExecuteTime(state.event);

export const getEventExecutionEvents = (state) => fromEvent.getExecutionEvents(state.event);

export const getEventsOptions = (state) => fromEvent.getEventsOptions(state.event);

export const isEventsExecuting = (state) => fromEvent.isExecuting(state.event);

export const getEventsExecuteId = (state) => fromEvent.getExecuteId(state.event);

export const getEventManualCode = (state) => fromEvent.getManualCode(state.event);

export const getEventIds = (state) => fromEvent.getIds(state.event);

export const getEventsGroupColumn = (state) => fromEvent.getGroupColumn(state.event);

export const getEventSql = (state, id) => fromEvent.getSql(state.event, id);

export const getEventExposureError = (state, id) => fromEvent.getError(state.event, id);

export const getEventName = (state, id) => fromEvent.getName(state.event, id);

export const getUniqueChar = (state, id) => fromEvent.getUniqueChar(state.event, id);

export const getPeriodsState = (state, id) => fromEvent.getPeriodsState(state.event, id);

export const getEventFilter = (state, id) => fromEvent.getFilter(state.event, id);

export const getEventAggregationColumn = (state, id) =>
  fromEvent.getAggregationColumn(state.event, id);

export const getEventAggregationType = (state, id) => fromEvent.getAggregationType(state.event, id);

export const getEventSplitColumn = (state, id) => fromEvent.getSplitColumn(state.event, id);

export const getEvents = (state) => fromEvent.getEvents(state.event);

export const getEventIdUsingTemplate = (state) => fromEvent.getIdUsingTemplate(state.event);

export const getEventsCsv = (state) => fromEvent.getEventsCsv(state.event);

export const getEventOthersFilter = (state, id) => fromEvent.getOthersFilter(state.event, id);

export const getDateFrom = (state) => fromGlobalFilter.getFrom(state.globalFilter);

export const getDateTo = (state) => fromGlobalFilter.getTo(state.globalFilter);

export const getLocation = (state) => fromLocation.getLocation(state.location);

export const getPreviousLocation = (state) =>
  fromPreviousLocation.getPreviousLocation(state.previousLocation);

export const getProjectZoneId = (state) => fromProject.getZoneId(state.project);

export const getProjectOriginalZoneId = (state) => fromProject.getOriginalZoneId(state.project);

export const getProjectLocale = (state) => fromProject.getLocale(state.project);

export const areProjectOptionsFetching = (state) => fromProject.areOptionsFetching(state.project);

export const getActualizationDate = (state) => fromProject.getActualizationDate(state.project);

export const getAmountCalculatedDate = (state) =>
  fromProject.getAmountCalculatedDate(state.project);

export const getProjectCurrency = (state) => fromProject.getProjectCurrency(state.project);

export const getRetentionComplexFirstEvent = (state) =>
  fromRetentionComplex.getFirstEvent(state.retentionComplex);

export const getRetentionComplexFirstEventFilter = (state) =>
  fromRetentionComplex.getFirstEventFilter(state.retentionComplex);

export const getRetentionComplexReturnEvent = (state) =>
  fromRetentionComplex.getReturnEvent(state.retentionComplex);

export const getRetentionComplexReturnEventFilter = (state) =>
  fromRetentionComplex.getReturnEventFilter(state.retentionComplex);

export const getRetentionComplexUseLatestProfile = (state) =>
  fromRetentionComplex.getUseLatestProfile(state.retentionComplex);

export const getRetentionComplexDimensionType = (state) =>
  fromRetentionComplex.getDimensionType(state.retentionComplex);

export const getRetentionComplexExecutionResult = (state) =>
  fromRetentionComplex.getExecutionResult(state.retentionComplex);

export const getRetentionComplexExecutedQuery = (state) =>
  fromRetentionComplex.getExecutedQuery(state.retentionComplex);

export const isRetentionComplexCustomSequenceEnabled = (state) =>
  fromRetentionComplex.isCustomSequenceEnabled(state.retentionComplex);

export const getRetentionComplexCustomSequenceType = (state) =>
  fromRetentionComplex.getCustomSequenceType(state.retentionComplex);

export const getRetentionComplexCustomSequence = (state) =>
  fromRetentionComplex.getCustomSequence(state.retentionComplex);

export const getComplexCustomSequenceDimensions = (state) =>
  fromRetentionComplex.getCustomSequenceDimensions(state.retentionComplex);

export const getRetentionComplexIdUsingTemplate = (state) =>
  fromRetentionComplex.getIdUsingTemplate(state.retentionComplex);

export const getRetentionComplexSQL = (state) =>
  fromRetentionComplex.getRetentionComplexSQL(state.retentionComplex);

export const getRetentionComplexExposureError = (state) =>
  fromRetentionComplex.getError(state.retentionComplex);

export const getRetentionComplexOptions = (state) =>
  fromRetentionComplex.getOptions(state.retentionComplex);

export const getRetentionComplexExecuteTime = (state) =>
  fromRetentionComplex.getExecuteTime(state.retentionComplex);

export const isRetentionComplexExecuting = (state) =>
  fromRetentionComplex.isExecuting(state.retentionComplex);

export const getRetentionComplexExecuteId = (state) =>
  fromRetentionComplex.getExecuteId(state.retentionComplex);

export const getRetentionFirstEvent = (state) => fromRetention.getFirstEvent(state.retention);

export const getRetentionReturnEvent = (state) => fromRetention.getReturnEvent(state.retention);

export const getRetentionDimensionType = (state) => fromRetention.getDimensionType(state.retention);

export const getRetentionSQL = (state) => fromRetention.getRetentionSQL(state.retention);

export const getRetentionExposureError = (state) => fromRetention.getError(state.retention);

export const getRetentionFilterFor = (state, eventIndex) =>
  fromRetentionComplex.getFilterFor(state.retentionComplex, eventIndex);

export const getRetentionExecutionResult = (state) =>
  fromRetention.getExecutionResult(state.retention);

export const getRetentionExecutedQuery = (state) => fromRetention.getExecutedQuery(state.retention);

export const getRetentionOptions = (state) => fromRetention.getOptions(state.retention);

export const getRetentionExecuteTime = (state) => fromRetention.getExecuteTime(state.retention);

export const isRetentionExecuting = (state) => fromRetention.isExecuting(state.retention);

export const getRetentionExecuteId = (state) => fromRetention.getExecuteId(state.retention);

export const getFieldPathsSearchEntry = (state) => fromFieldPaths.getSearchEntry(state.fieldPaths);

export const isMappingFetching = (state) => fromMapping.isFetching(state.mapping);

export const getMappingCode = (state) => fromMapping.getCode(state.mapping);

export const getMappings = (state) => fromMapping.getMappings(state.mapping);

export const isMappingUpdating = (state) => fromMapping.isUpdating(state.mapping);

export const getMappingUpdatingCode = (state) => fromMapping.getUpdatingCode(state.mapping);

// Permissions текущего tenant'а из карты permissionsByTenant. Мемоизируем, чтобы Set был
// стабилен по ссылке между рендерами (иначе лишние перерисовки у потребителей).
export const getMyPermissions = createSelector(
  (state) => fromAuthorization.getPermissionsByTenant(state.authorization),
  getTenantId,
  (permissionsByTenant, tenantId) => new Set(permissionsByTenant[tenantId] || [])
);

export const isFetchingMyPermissions = (state) =>
  fromAuthorization.isFetchingMyPermissions(state.authorization);

export const getAuthorizationUpdatingGroup = (state) =>
  fromAuthorization.getUpdatingGroup(state.authorization);

export const isAuthorizationUpdatingGroup = (state) =>
  fromAuthorization.isUpdatingGroup(state.authorization);

export const getAuthorizationGroups = (state) => fromAuthorization.getGroups(state.authorization);

export const isAuthorizationFetchingGroups = (state) =>
  fromAuthorization.isFetchingGroups(state.authorization);

export const isAuthorizationDeletingEntity = (state) =>
  fromAuthorization.isDeletingEntity(state.authorization);

export const getTabKey = (state) => fromAuthorization.getTabKey(state.authorization);

export const getApplicationError = (state) => fromApplication.getErrorMessage(state.application);

export const isApplicationReady = (state) => fromApplication.isReady(state.application);

export const sideMenuCollapsed = (state) => fromApplication.sideMenuCollapsed(state.application);

export const getUsageEvents = (state) => fromUsage.getExecutionEvents(state.usage);

export const getUsageChartOptions = (state) => fromUsage.getChartOptions(state.usage);

export const getUsageExecuteTime = (state) => fromUsage.getExecuteTime(state.usage);

export const isUsageExecuting = (state) => fromUsage.isExecuting(state.usage);

export const getUsageExecuteId = (state) => fromUsage.getExecuteId(state.usage);

export const getUsageSessions = (state) => fromUsage.getSessions(state.usage);

export const getUsageAvgSessionLength = (state) => fromUsage.getAvgSessionLength(state.usage);

export const getUsageUniqueUsers = (state) => fromUsage.getUniqueUsers(state.usage);

export const getUsageQueryType = (state) => fromUsage.getQueryType(state.usage);

export const getUsageGroupColumn = (state) => fromUsage.getGroupColumn(state.usage);

export const getUsageSplitColumn = (state) => fromUsage.getSplitColumn(state.usage);

export const getRevenueEvents = (state) => fromRevenue.getExecutionEvents(state.revenue);

export const getRevenue = (state) => fromRevenue.getRevenue(state.revenue);

export const getRevenueUniqueUsers = (state) => fromRevenue.getUniqueUsers(state.revenue);

export const getRevenueExecuteTime = (state) => fromRevenue.getExecuteTime(state.revenue);

export const getRevenueCurrencySymbol = (state) =>
  fromRevenue.getRevenueCurrencySymbol(state.revenue);

export const isRevenueExecuting = (state) => fromRevenue.isExecuting(state.revenue);

export const getRevenueExecuteId = (state) => fromRevenue.getExecuteId(state.revenue);

export const getRevenueChartOptions = (state) => fromRevenue.getOptions(state.revenue);

export const getRevenuePayers = (state) => fromRevenue.getPayers(state.revenue);

export const getRevenueGroupColumn = (state) => fromRevenue.getGroupColumn(state.revenue);

export const getRevenueAmountField = (state) => fromRevenue.getAmountField(state.revenue);

export const getRevenueAmountFieldPath = (state) => getEventFieldPath(getRevenueAmountField(state));

export const getRevenueAmountFields = (state) => fromRevenue.getAmountFields(state.revenue);

export const getRevenueSplitColumn = (state) => fromRevenue.getSplitColumn(state.revenue);

export const getRevenueQueryType = (state) => fromRevenue.getQueryType(state.revenue);

export const getExecutedQueryType = (state) => fromRevenue.getExecutedQueryType(state.revenue);

export const getRevenuePaymentFilter = (state) => fromRevenue.getPaymentFilter(state.revenue);

export const getLabels = (state) => fromLabels.getLabels(state.labels);

export const getLabel = (state, id) => fromLabels.getLabels(state.labels, id);

export const areLabelsFetching = (state) => fromLabels.areFetching(state.labels);

export const isLabelsModalVisible = (state) => fromLabels.isModalVisible(state.labels);

export const getLabelId = (state) => fromLabels.getId(state.labels);

export const getLabelTitle = (state) => fromLabels.getTitle(state.labels);

export const getLabelDescription = (state) => fromLabels.getDescription(state.labels);

export const getLabelDuration = (state) => fromLabels.getDuration(state.labels);

export const getLabelColor = (state) => fromLabels.getColor(state.labels);

export const areLabelsSaving = (state) => fromLabels.areSaving(state.labels);

export const getCurrentDateFromLabels = (state) => fromLabels.getCurrentDate(state.labels);

export const isCacheEnabled = (state) => fromCache.isCacheEnabled(state.cache);

export const getUsersExecutionEvent = (state) => fromUsers.getExecutionEvent(state.users);

export const isUsersExecuting = (state) => fromUsers.isExecuting(state.users);

export const getUsersExecuteId = (state) => fromUsers.getExecuteId(state.users);

export const isUsersAggregateEnabled = (state) => fromUsers.isAggregateEnabled(state.users);

export const getUsersIdUsingTemplate = (state) => fromUsers.getIdUsingTemplate(state.users);

export const getUsersFilter = (state) => fromUsers.getUserFilter(state.users);

export const getUsersSQL = (state) => fromUsers.getUsersSQL(state.users);

export const getUsersExposureError = (state) => fromUsers.getError(state.users);

export const getDailyCohortRange = (state) => fromMeta.getDailyRange(state.meta);

export const getWeeklyCohortRange = (state) => fromMeta.getWeeklyRange(state.meta);

export const getMonthlyCohortRange = (state) => fromMeta.getMonthlyRange(state.meta);

export const getDashboards = (state) => fromDashboards.getDashboards(state.dashboards);

export const getTemplatesState = (state) => fromDashboards.getTemplatesState(state.dashboards);

export const getUnitsExecutionState = (state) =>
  fromDashboards.getUnitsExecutionState(state.dashboards);

export const getSelectedDashboard = (state) =>
  fromDashboards.getSelectedDashboard(state.dashboards);

export const getIsUpdatingDashboard = (state) =>
  fromDashboards.getIsUpdatingDashboard(state.dashboards);

export const getEditingDashboard = (state) => fromDashboards.getEditingDashboard(state.dashboards);

export const getExecuteEditingDashboard = (state) =>
  fromDashboards.getExecuteEditingDashboard(state.dashboards);

export const getDashboardName = (state) => fromDashboards.getDashboardName(state.dashboards);

export const getDashboardEditingMode = (state) => fromDashboards.getEditingMode(state.dashboards);

export const getDashboardCreatingMode = (state) => fromDashboards.getCreatingMode(state.dashboards);

export const getExecuteQueries = (state) => fromDashboards.getQueries(state.dashboards);

export const getDashboardsUpdateTime = (state) => fromDashboards.getUpdateTime(state.dashboards);

export const getDashboardsExtended = (state) => fromDashboards.getExtended(state.dashboards);

export const getTemplates = (state) => fromDashboards.getTemplates(state.dashboards);

export const getUnits = (state) => fromDashboards.getUnits(state.dashboards);

export const getDashboardEvenIds = (state, unitId) =>
  fromDashboards.getIds(state.dashboards, unitId);

export const getDashboardEventsGroupColumn = (state, unitId) =>
  fromDashboards.getGroupColumn(state.dashboards, unitId);

export const getDashboardEventSql = (state, unitId, id) =>
  fromDashboards.getSql(state.dashboards, unitId, id);

export const getDashboardEventExposureError = (state, unitId, id) =>
  fromDashboards.getError(state.dashboards, unitId, id);

export const getDashboardEventName = (state, unitId, id) =>
  fromDashboards.getName(state.dashboards, unitId, id);

export const getDashboardUniqueChar = (state, unitId, id) =>
  fromDashboards.getUniqueChar(state.dashboards, unitId, id);

export const getDashboardEventPeriodsState = (state, unitId, id) =>
  fromDashboards.getPeriodsState(state.dashboards, unitId, id);

export const getDashboardEventFilter = (state, unitId, id) =>
  fromDashboards.getFilter(state.dashboards, unitId, id);

export const getDashboardEventAggregationColumn = (state, unitId, id) =>
  fromDashboards.getAggregationColumn(state.dashboards, unitId, id);

export const getDashboardEventAggregationType = (state, unitId, id) =>
  fromDashboards.getAggregationType(state.dashboards, unitId, id);

export const getDashboardEventSplitColumn = (state, unitId, id) =>
  fromDashboards.getSplitColumn(state.dashboards, unitId, id);

export const getDashboardEvents = (state, unitId) =>
  fromDashboards.getEvents(state.dashboards, unitId);

export const getDashboardEventIdUsingTemplate = (state, unitId) =>
  fromDashboards.getIdUsingTemplate(state.dashboards, unitId);

export const getDashboardEventsCsv = (state, unitId) =>
  fromDashboards.getEventsCsv(state.dashboards, unitId);

export const getDashboardEventOthersFilter = (state, unitId, id) =>
  fromDashboards.getOthersFilter(state.dashboards, unitId, id);

export const getDashboardEventView = (state, unitId) =>
  fromDashboards.getEventView(state.dashboards, unitId);

export const getDashboardEnteredFormula = (state, unitId) =>
  fromDashboards.getEnteredFormula(state.dashboards, unitId);

export const getDashboardEnteredLegendName = (state, unitId) =>
  fromDashboards.getEnteredLegendName(state.dashboards, unitId);

export const getIsFormulaOnly = (state, unitId) =>
  fromDashboards.getIsFormulaOnly(state.dashboards, unitId);

export const getDashboardEventOptions = (state, unitId) =>
  fromDashboards.getEventOptions(state.dashboards, unitId);

export const getDashboardRetentionSimpleFirstEvent = (state, unitId) =>
  fromDashboards.getDashboardRetentionSimpleFirstEvent(state.dashboards, unitId);

export const getDashboardRetentionSimpleReturnEvent = (state, unitId) =>
  fromDashboards.getDashboardRetentionSimpleReturnEvent(state.dashboards, unitId);

export const getDashboardRetentionSimpleDimensionType = (state, unitId) =>
  fromDashboards.getDashboardRetentionSimpleDimensionType(state.dashboards, unitId);

export const getDashboardRetentionSimpleOptions = (state, unitId) =>
  fromDashboards.getDashboardRetentionSimpleOptions(state.dashboards, unitId);

export const getDashboardsRetentionFirstEvent = (state, unitId) =>
  fromDashboards.getFirstEvent(state.dashboards, unitId);

export const getDashboardsRetentionFirstEventFilter = (state, unitId) =>
  fromDashboards.getFirstEventFilter(state.dashboards, unitId);

export const getDashboardsRetentionReturnEvent = (state, unitId) =>
  fromDashboards.getReturnEvent(state.dashboards, unitId);

export const getDashboardsRetentionReturnEventFilter = (state, unitId) =>
  fromDashboards.getReturnEventFilter(state.dashboards, unitId);

export const getDashboardsRetentionUseLatestProfile = (state, unitId) =>
  fromDashboards.getUseLatestProfile(state.dashboards, unitId);

export const getDashboardsRetentionDimensionType = (state, unitId) =>
  fromDashboards.getDimensionType(state.dashboards, unitId);

export const getDashboardsRetentionFilterFor = (state, unitId, eventIndex) =>
  fromDashboards.getFilterFor(state.dashboards, unitId, eventIndex);

export const isDashboardsRetentionCustomSequenceEnabled = (state, unitId) =>
  fromDashboards.isCustomSequenceEnabled(state.dashboards, unitId);

export const getDashboardsRetentionCustomSequenceType = (state, unitId) =>
  fromDashboards.getCustomSequenceType(state.dashboards, unitId);

export const getDashboardsRetentionCustomSequence = (state, unitId) =>
  fromDashboards.getCustomSequence(state.dashboards, unitId);

export const getDashboardsRetentionCustomSequenceDimensions = (state, unitId) =>
  fromDashboards.getCustomSequenceDimensions(state.dashboards, unitId);

export const getDashboardsRetentionIdUsingTemplate = (state, unitId) =>
  fromDashboards.getIdUsingTemplate(state.dashboards, unitId);

export const getDashboardsRetentionComplexSQL = (state, unitId) =>
  fromDashboards.getRetentionComplexSQL(state.dashboards, unitId);

export const getDashboardsRetentionError = (state, unitId) =>
  fromDashboards.getError(state.dashboards, unitId);

export const getDashboardRetentionComplexOptions = (state, unitId) =>
  fromDashboards.getRetentionComplexOptions(state.dashboards, unitId);

export const getDashboardsUsersEvent = (state, unitId) =>
  fromDashboards.getEvent(state.dashboards, unitId);

export const getDashboardsUsersColumns = (state, unitId) =>
  fromDashboards.getColumns(state.dashboards, unitId);

export const isDashboardsUsersAggregateEnabled = (state, unitId) =>
  fromDashboards.isAggregateEnabled(state.dashboards, unitId);

export const getDashboardsUsersFilter = (state, unitId) =>
  fromDashboards.getUserFilter(state.dashboards, unitId);

export const getDashboardsUsersIdUsingTemplate = (state, unitId) =>
  fromDashboards.getIdUsingUsersTemplate(state.dashboards, unitId);

export const getDashboardsUsersSQL = (state, unitId) =>
  fromDashboards.getUsersSQL(state.dashboards, unitId);

export const getDashboardsUsersError = (state, unitId) =>
  fromDashboards.getUsersError(state.dashboards, unitId);

export const getDashboardsRevenueAmountField = (state, unitId) =>
  fromDashboards.getAmountField(state.dashboards, unitId);

export const getDashboardsRevenueAmountFields = (state, unitId) =>
  fromDashboards.getAmountFields(state.dashboards, unitId);

export const getDashboardsRevenueGroupColumn = (state, unitId) =>
  fromDashboards.getRevenueGroupColumn(state.dashboards, unitId);

export const getDashboardsRevenueSplitColumn = (state, unitId) =>
  fromDashboards.getRevenueSplitColumn(state.dashboards, unitId);

export const getDashboardsRevenueQueryType = (state, unitId) =>
  fromDashboards.getQueryType(state.dashboards, unitId);

export const getDashboardsRevenuePaymentFilter = (state, unitId) =>
  fromDashboards.getPaymentFilter(state.dashboards, unitId);

export const getDashboardRevenueOptions = (state, unitId) =>
  fromDashboards.getOptions(state.dashboards, unitId);

export const isReportFetching = (state) => fromReports.isFetching(state.reports);

export const isUpdatingFunnelDashboard = (state, unitId) =>
  fromDashboards.isUpdating(state.dashboards, unitId);

export const getUpdatingFunnelDashboard = (state, unitId) =>
  fromDashboards.getUpdating(state.dashboards, unitId);

export const getUpdatingFunnelIdDashboard = (state, unitId) =>
  fromDashboards.getUpdatingId(state.dashboards, unitId);

export const getDashboardFunnelConversionInterval = (state, unitId) =>
  fromDashboards.getFunnelConversionInterval(state.dashboards, unitId);

export const getDashboardFunnelView = (state, unitId) =>
  fromDashboards.getFunnelView(state.dashboards, unitId);

export const getParams = (state) => ({
  tenantId: getTenantId(state),
  backendUrl: getApplicationBackendUrl(state),
});

export const getJobIdsByExecuteId = (state, executedId) =>
  fromJobIds.getIdsByExecutedId(state.jobIds, executedId);

export const getExecuteIdsFromJobIds = (state) => fromJobIds.getExecuteIds(state.jobIds);

export const getCSVExecuteDataFromJobIds = (state) => fromJobIds.getCSVExecuteData(state.jobIds);

export const getFilteredTemplatesByType = (state, templateType) => {
  const templates = getTemplates(state);
  return Array.from(
    Object.values(templates)
      .filter((template) => {
        return template.type === templateType;
      })
      .sort((a, b) => {
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      })
  );
};

export const getSortedTemplates = (state) => {
  const templates = getTemplates(state);
  return Array.from(
    Object.values(templates).sort((a, b) => {
      return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
    })
  );
};
